import { lineLimit } from "./shop-options";

// The bag as stored in the buyer's browser, and the counts the header shows from it. No schemas
// here: every page of a creator site with a shop loads this for the header's bag.

export type BagItem = { productId: string; variantId: string; quantity: number };
/** The most different items one bag may hold (the cart contract allows 30). */
export const bagLimit = 30;

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const sameBagItem = (
  a: Pick<BagItem, "productId" | "variantId">,
  b: Pick<BagItem, "productId" | "variantId">,
) => a.productId === b.productId && a.variantId === b.variantId;

/** Reads whatever was stored and keeps only what the cart contract accepts. */
export function parseBag(raw: string | null | undefined): BagItem[] {
  let stored: unknown;
  try {
    stored = JSON.parse(raw || "[]");
  } catch {
    return [];
  }
  if (!Array.isArray(stored)) return [];
  const bag: BagItem[] = [];
  for (const entry of stored) {
    if (!entry || typeof entry !== "object") continue;
    const { productId, variantId, quantity } = entry as Record<string, unknown>;
    if (typeof productId !== "string" || !uuid.test(productId)) continue;
    if (typeof variantId !== "string" || !uuid.test(variantId)) continue;
    if (typeof quantity !== "number" || !Number.isInteger(quantity) || quantity < 1) continue;
    const existing = bag.find((item) => sameBagItem(item, { productId, variantId }));
    if (existing) existing.quantity = Math.min(lineLimit, existing.quantity + quantity);
    else if (bag.length < bagLimit)
      bag.push({ productId, variantId, quantity: Math.min(lineLimit, quantity) });
  }
  return bag;
}

export function bagCount(bag: readonly BagItem[]) {
  return bag.reduce((total, item) => total + item.quantity, 0);
}

export function itemCount(count: number) {
  return count === 1 ? "1 item" : `${count} items`;
}

export function bagLabel(count: number) {
  return count ? `Bag, ${itemCount(count)}` : "Bag, empty";
}
